import { Injectable, inject } from '@angular/core';
import { UserRole } from '../../features/activity-dashboard/activity-dashboard.enums';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class CmsRoleAccessService {
  private readonly authService = inject(AuthService);

  private readonly elevatedRoles: UserRole[] = [
    UserRole.DistricSettingsAdmin,
    UserRole.LocalSystemAdmin,
    UserRole.SystemAdmin,
  ];

  private readonly cmsRoles: UserRole[] = [
    UserRole.CMSOwner,
    UserRole.CMSManager,
    UserRole.CMSDataEntry,
    UserRole.CMSBrowser,
  ];

  /**
   * DSA/LSA/System Admin (or super admin) always have access. Otherwise, only the
   * user's CMS-family roles matter — roles outside that family (Dashboard User,
   * Fidelity Checker, etc.) are ignored. Access is granted if any held CMS role
   * is not in `restrictedCmsRoles` (union of CMS role permissions).
   *
   * `isSuperAdmin` defaults to the current user's status from `AuthService` so
   * callers don't have to remember to pass it; pass it explicitly only to
   * override (e.g. checking access for a different user).
   */
  computeAllowFromCmsRole(
    userRoles: string[],
    restrictedCmsRoles: UserRole[],
    isSuperAdmin: boolean = this.authService.userData.value?.isSuperAdmin ??
      false
  ): boolean {
    if (
      isSuperAdmin ||
      userRoles.some(role => this.elevatedRoles.includes(role as UserRole))
    ) {
      return true;
    }
    const userCmsRoles = userRoles.filter(role =>
      this.cmsRoles.includes(role as UserRole)
    );
    return userCmsRoles.some(
      role => !restrictedCmsRoles.includes(role as UserRole)
    );
  }
}
